import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { MapView } from '@/components/Map';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import {
  PanelRightClose, PanelRightOpen, MapPin, Car, Clock,
  ChevronLeft, ChevronRight, RotateCcw, AlertTriangle,
  ShieldAlert, Hotel, Store, Radio, Users, Phone, Bus,
} from 'lucide-react';
import { Link } from 'wouter';
import { ContactRow } from '@/components/ContactRow';
import { formatDominicaPhone } from '@/lib/formatPhone';
import { BookingForChip } from '@/components/PassengerAge';

// ─── Types ───────────────────────────────────────────────────────────────────

interface RideDriver {
  id: string;
  full_name: string;
  lat: number | null;
  lng: number | null;
  vehicle_plate: string | null;
  phone_number?: string | null;
  profile_picture_url?: string | null;
}

interface ActiveRide {
  id: string;
  status: 'searching' | 'accepted' | 'in_progress';
  passenger_name: string;
  passenger_phone?: string;
  passenger_profile_picture_url?: string | null;
  driver: RideDriver | null;
  origin_lat: number;
  origin_lng: number;
  destination_lat: number;
  destination_lng: number;
  /** Rider's pickup / drop-off pin when set, otherwise the stop. */
  pickup_lat?: number | null;
  pickup_lng?: number | null;
  dropoff_lat?: number | null;
  dropoff_lng?: number | null;
  pickup_is_custom?: boolean;
  dropoff_is_custom?: boolean;
  dropoff_notes?: string | null;
  origin_label: string | null;
  destination_label: string | null;
  origin_name?: string | null;
  destination_name?: string | null;
  fare_amount: number | null;
  requested_at: string;
  accepted_at?: string | null;
  picked_up_at?: string | null;
  // Book for someone else
  on_behalf_of_name?: string | null;
  on_behalf_of_age_band?: string | null;
  on_behalf_of_notes?: string | null;
}

interface OnlineDriver {
  id: string;
  full_name: string;
  license_plate: string | null;
  corridor_id: string | null;
  current_direction: 'forward' | 'reverse' | null;
  seats_available: number;
  lat: number;
  lng: number;
  last_location_updated_at: string;
  staleness_seconds: number;
  is_busy: boolean;
  // Extended fields from list_online_drivers_admin fallback
  phone_number?: string | null;
  current_route_origin?: string | null;
  current_route_destination?: string | null;
  avatar_url?: string | null;
  profile_picture_url?: string | null;
  vehicle_make?: string | null;
  vehicle_model?: string | null;
}

interface SOSEvent {
  id: string;
  user_id: string;
  user_role: string;
  ride_id: string | null;
  created_at: string;
  user_name?: string;
  lat?: number;
  lng?: number;
}

interface IncidentPin {
  id: string;
  severity: string;
  status: string;
  subject_name: string;
  subject_role: string;
  description: string;
  lat?: number;
  lng?: number;
  created_at: string;
}

interface ConciergePin {
  id: string;
  guest_name: string;
  hotel_name: string;
  status: string;
  pickup_lat?: number;
  pickup_lng?: number;
  created_at: string;
}

interface BusinessPin {
  id: string;
  name: string;
  category: string;
  is_featured: boolean;
  active: boolean;
  latitude: number;
  longitude: number;
}

interface RidesResponse {
  ok: boolean;
  generated_at: string;
  rides: ActiveRide[];
}

// ─── Layer config ────────────────────────────────────────────────────────────

type LayerKey = 'searching' | 'accepted' | 'in_progress' | 'drivers' | 'businesses' | 'sos' | 'incidents' | 'concierge';

interface LayerConfig {
  key: LayerKey;
  label: string;
  color: string;
  defaultOn: boolean;
}

const LAYERS: LayerConfig[] = [
  { key: 'searching', label: 'Searching rides', color: '#fbbf24', defaultOn: true },
  { key: 'accepted', label: 'Accepted rides', color: '#3b82f6', defaultOn: true },
  { key: 'in_progress', label: 'In-progress rides', color: '#10b981', defaultOn: true },
  { key: 'drivers', label: 'Online drivers', color: '#059669', defaultOn: true },
  { key: 'businesses', label: 'Featured business pins', color: '#f97316', defaultOn: false },
  { key: 'sos', label: 'Recent SOS events', color: '#ef4444', defaultOn: false },
  { key: 'incidents', label: 'Open incident reports', color: '#f59e0b', defaultOn: false },
  { key: 'concierge', label: 'Concierge requests', color: '#8b5cf6', defaultOn: false },
];

const LAYER_ICONS: Record<LayerKey, typeof MapPin> = {
  searching: Radio,
  accepted: Car,
  in_progress: Car,
  drivers: Car,
  businesses: Store,
  sos: AlertTriangle,
  incidents: ShieldAlert,
  concierge: Hotel,
};

// ─── Corridors ───────────────────────────────────────────────────────────────

const CORRIDORS = [
  { name: 'Roseau ↔ Portsmouth', stops: ['Roseau', 'Canefield', 'Massacre', 'Mahaut', 'St Joseph', 'Layou', 'Mero', 'Salisbury', 'Coulibistrie', 'Colihaut', 'Dublanc', 'Bioche', 'Picard', 'Portsmouth'] },
  { name: 'Roseau ↔ Marigot', stops: ['Roseau', 'Goodwill', 'Fond Cole', 'Canefield', 'Pond Casse', 'Marigot'] },
  { name: 'Roseau ↔ La Plaine', stops: ['Roseau', 'Loubiere', 'Pointe Michel', 'Soufriere', 'Scotts Head', 'Grand Bay', 'Stowe', 'Petite Savanne', 'La Plaine'] },
  { name: 'Roseau ↔ Castle Bruce', stops: ['Roseau', 'Canefield', 'Pond Casse', 'Castle Bruce'] },
  { name: 'Roseau ↔ Grand Bay', stops: ['Roseau', 'Loubiere', 'Pointe Michel', 'Soufriere', 'Scotts Head', 'Grand Bay'] },
  { name: 'Portsmouth ↔ Marigot', stops: ['Portsmouth', 'Calibishie', 'Wesley', 'Woodford Hill', 'Marigot'] },
];

// ─── Time windows ────────────────────────────────────────────────────────────

type TimeWindow = '1h' | '4h' | '24h' | '7d';
const TIME_WINDOWS: { key: TimeWindow; label: string; ms: number }[] = [
  { key: '1h', label: '1h', ms: 3600000 },
  { key: '4h', label: '4h', ms: 14400000 },
  { key: '24h', label: '24h', ms: 86400000 },
  { key: '7d', label: '7d', ms: 604800000 },
];

// ─── LocalStorage helpers ────────────────────────────────────────────────────

const LS_LAYERS_KEY = 'hplate-admin-livemap-layers';
const LS_WINDOW_KEY = 'hplate-admin-livemap-window';

function loadLayerState(): Record<LayerKey, boolean> {
  try {
    const stored = localStorage.getItem(LS_LAYERS_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  const defaults: Record<string, boolean> = {};
  LAYERS.forEach((l) => { defaults[l.key] = l.defaultOn; });
  return defaults as Record<LayerKey, boolean>;
}

function loadTimeWindow(): TimeWindow {
  try {
    const stored = localStorage.getItem(LS_WINDOW_KEY);
    if (stored && ['1h', '4h', '24h', '7d'].includes(stored)) return stored as TimeWindow;
  } catch {}
  return '24h';
}

// ─── Status helpers ──────────────────────────────────────────────────────────

const STATUS_ORDER: Record<string, number> = { searching: 0, accepted: 1, in_progress: 2 };
const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  searching: { bg: 'bg-amber-500/10', text: 'text-amber-400', dot: '#fbbf24' },
  accepted: { bg: 'bg-blue-500/10', text: 'text-blue-400', dot: '#3b82f6' },
  in_progress: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', dot: '#10b981' },
};

const STATUS_LABELS: Record<string, string> = {
  searching: 'Searching',
  accepted: 'Accepted',
  in_progress: 'In Progress',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m`;
  return `${Math.floor(hours / 24)}d`;
}

// ─── Dark map style ──────────────────────────────────────────────────────────

const DARK_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1d2c4d' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a3646' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3b9' }] },
  { featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ color: '#4b6878' }] },
  { featureType: 'land', elementType: 'geometry', stylers: [{ color: '#1d2c4d' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#304a7d' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#255763' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2c6675' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1626' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4e6d70' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#283d6a' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#6f9ba5' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f3948' }] },
];

// ─── Ride geometry ───────────────────────────────────────────────────────────

type LatLng = { lat: number; lng: number };

/** Where the bus picks the rider up: their pin if set, otherwise the stop. */
function ridePickup(ride: ActiveRide): LatLng {
  return { lat: ride.pickup_lat ?? ride.origin_lat, lng: ride.pickup_lng ?? ride.origin_lng };
}

/** Where the rider gets off: their moved pin if set, otherwise the stop. */
function rideDropoff(ride: ActiveRide): LatLng {
  return { lat: ride.dropoff_lat ?? ride.destination_lat, lng: ride.dropoff_lng ?? ride.destination_lng };
}

/** Rough distance in metres, accurate enough at Dominica's scale. */
function distanceMeters(a: LatLng, b: LatLng): number {
  const dLat = (b.lat - a.lat) * 111_320;
  const dLng = (b.lng - a.lng) * 111_320 * Math.cos((a.lat * Math.PI) / 180);
  return Math.hypot(dLat, dLng);
}

/** Re-route the bus-to-pickup line only after the bus has moved this far. */
const APPROACH_REROUTE_METERS = 400;

function roadRouteKey(from: LatLng, to: LatLng): string {
  return `${from.lat.toFixed(5)},${from.lng.toFixed(5)}>${to.lat.toFixed(5)},${to.lng.toFixed(5)}`;
}

/** Road route from `from` to `to`, or null when Directions is unavailable. */
function fetchRoadRoute(from: LatLng, to: LatLng): Promise<LatLng[] | null> {
  return new Promise((resolve) => {
    if (!window.google?.maps?.DirectionsService) {
      resolve(null);
      return;
    }
    new window.google.maps.DirectionsService().route(
      { origin: from, destination: to, travelMode: window.google.maps.TravelMode.DRIVING },
      (result, status) => {
        const path = status === 'OK' ? result?.routes?.[0]?.overview_path : null;
        resolve(path && path.length > 1 ? path.map((p) => ({ lat: p.lat(), lng: p.lng() })) : null);
      },
    );
  });
}

// ─── Driver bus marker ───────────────────────────────────────────────────────

/** A driver's active rides, split by whether the rider is already on board. */
interface DriverTrips {
  onBoard: ActiveRide[];
  toPickUp: ActiveRide[];
}

const EMPTY_TRIPS: DriverTrips = { onBoard: [], toPickUp: [] };

/** Groups active rides by driver id. */
function groupTripsByDriver(rides: ActiveRide[]): Map<string, DriverTrips> {
  const byDriver = new Map<string, DriverTrips>();
  rides.forEach((ride) => {
    if (!ride.driver) return;
    const trips = byDriver.get(ride.driver.id) ?? { onBoard: [], toPickUp: [] };
    if (ride.status === 'in_progress') trips.onBoard.push(ride);
    else if (ride.status === 'accepted') trips.toPickUp.push(ride);
    byDriver.set(ride.driver.id, trips);
  });
  return byDriver;
}

/** "3 on board · 1 to pick up", or "Free" when the driver has no riders. */
function tripSummary(trips: DriverTrips): string {
  const parts: string[] = [];
  if (trips.onBoard.length) parts.push(`${trips.onBoard.length} on board`);
  if (trips.toPickUp.length) parts.push(`${trips.toPickUp.length} to pick up`);
  return parts.length ? parts.join(' · ') : 'Free';
}

/** Green when carrying riders, blue when heading to a pickup, gray when free. */
function tripColor(trips: DriverTrips): string {
  if (trips.onBoard.length) return '#10b981';
  if (trips.toPickUp.length) return '#3b82f6';
  return '#6b7280';
}

const BUS_ICON_SVG =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.4" ' +
  'stroke-linecap="round" stroke-linejoin="round"><path d="M8 6v6"/><path d="M15 6v6"/>' +
  '<path d="M2 12h19.6"/><path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3"/>' +
  '<circle cx="7" cy="18" r="2"/><path d="M9 18h5"/><circle cx="16" cy="18" r="2"/></svg>';

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

/**
 * Bus pin with the driver's first name and what they're doing, always visible.
 * `offline` is for a driver with riders whose app is no longer online: the pin
 * sits at the last known position.
 */
function buildBusMarkerEl(name: string, trips: DriverTrips, staleSeconds: number, offline = false): HTMLDivElement {
  const stale = offline || staleSeconds >= 60;
  const color = offline ? '#9ca3af' : stale ? '#f59e0b' : tripColor(trips);
  const firstName = escapeHtml(name.trim().split(/\s+/)[0] || 'Driver');
  const detail = offline
    ? `Offline · ${tripSummary(trips)}`
    : stale
      ? `GPS ${staleSeconds >= 3600 ? `${Math.floor(staleSeconds / 3600)}h` : `${Math.floor(staleSeconds / 60)}m`} ago`
      : tripSummary(trips);
  const el = document.createElement('div');
  el.style.cursor = 'pointer';
  el.style.opacity = offline || staleSeconds >= 300 ? '0.6' : '1';
  el.innerHTML = `
    <div style="display:flex;flex-direction:column;align-items:center;">
      <div style="display:flex;align-items:center;gap:6px;background:#111827;border:2px ${stale ? 'dashed' : 'solid'} ${color};border-radius:999px;padding:3px 10px 3px 3px;box-shadow:0 3px 12px rgba(0,0,0,0.35);white-space:nowrap;">
        <div style="width:26px;height:26px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;">${BUS_ICON_SVG}</div>
        <div style="line-height:1.1;">
          <div style="font-size:12px;font-weight:700;color:#fff;">${firstName}</div>
          <div style="font-size:10px;font-weight:600;color:${color};">${escapeHtml(detail)}</div>
        </div>
      </div>
      <div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:7px solid ${color};"></div>
    </div>`;
  return el;
}

// ─── Pin detail type ─────────────────────────────────────────────────────────

type PinDetail =
  | { type: 'ride'; data: ActiveRide }
  | { type: 'driver'; data: OnlineDriver }
  | { type: 'sos'; data: SOSEvent }
  | { type: 'incident'; data: IncidentPin }
  | { type: 'concierge'; data: ConciergePin }
  | { type: 'business'; data: BusinessPin };

// ─── Component ───────────────────────────────────────────────────────────────

export default function LiveMap() {
  // Layer state
  const [layers, setLayers] = useState<Record<LayerKey, boolean>>(loadLayerState);
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(loadTimeWindow);
  const [corridor, setCorridor] = useState<string>('all');
  const [filterPanelOpen, setFilterPanelOpen] = useState(true);

  // Charter KPIs — read from admin_list_charter_requests so the
  // operator can see at a glance whether the private-charter feed is
  // alive without leaving the corridor map. Pin rendering for charter
  // pickups is intentionally deferred to the dedicated /admin/charter
  // page (richer ergonomics there); this is the at-a-glance summary.
  const [charterKpis, setCharterKpis] = useState<{ open: number; bidding_closed: number; assigned: number } | null>(null);

  // Data
  const [rides, setRides] = useState<ActiveRide[]>([]);
  const [onlineDrivers, setOnlineDrivers] = useState<OnlineDriver[]>([]);
  const [sosEvents, setSosEvents] = useState<SOSEvent[]>([]);
  const [incidents, setIncidents] = useState<IncidentPin[]>([]);
  const [conciergeReqs, setConciergeReqs] = useState<ConciergePin[]>([]);
  const [businesses, setBusinesses] = useState<BusinessPin[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(true);
  const [selectedPin, setSelectedPin] = useState<PinDetail | null>(null);
  // Surfaces rides-RPC failures (e.g. enum drift, RLS regression) so we never
  // silently render 0/0/0 KPIs again. Cleared on the next successful fetch.
  const [ridesError, setRidesError] = useState<string | null>(null);
  const ridesErrorToastedRef = useRef(false);

  /** The open ride card follows the latest data (stop changes, status) instead of the click-time copy. */
  const liveSelectedPin = useMemo<PinDetail | null>(() => {
    if (selectedPin?.type === 'ride') {
      const fresh = rides.find((r) => r.id === selectedPin.data.id);
      return fresh ? { type: 'ride', data: fresh } : selectedPin;
    }
    if (selectedPin?.type === 'driver') {
      const fresh = onlineDrivers.find((d) => d.id === selectedPin.data.id);
      return fresh ? { type: 'driver', data: fresh } : selectedPin;
    }
    return selectedPin;
  }, [selectedPin, rides, onlineDrivers]);

  /** The open ride dropped out of the active feed: dropped off or canceled. */
  const selectedRideEnded =
    selectedPin?.type === 'ride' && !loading && !ridesError && !rides.some((r) => r.id === selectedPin.data.id);

  const driverTrips = useMemo(() => groupTripsByDriver(rides), [rides]);

  // Map refs
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<string, google.maps.marker.AdvancedMarkerElement>>(new Map());
  const polylinesRef = useRef<Map<string, google.maps.Polyline>>(new Map());
  /** Road path per pickup→drop-off pair; null while loading or when Directions fails. */
  const roadRoutesRef = useRef<Map<string, LatLng[] | null>>(new Map());
  const [roadRouteTick, setRoadRouteTick] = useState(0);
  /** Road path from a bus to a pickup, keyed by driver + pickup; `from` is where the bus was when routed. */
  const approachRoutesRef = useRef<Map<string, { from: LatLng; path: LatLng[] | null; pending: boolean }>>(new Map());
  const channelsRef = useRef<Map<string, any>>(new Map());
  const driverThrottleRef = useRef<Map<string, number>>(new Map());
  const clustererRef = useRef<MarkerClusterer | null>(null);

  // ─── Persist layer/time state ──────────────────────────────────────────────

  useEffect(() => {
    localStorage.setItem(LS_LAYERS_KEY, JSON.stringify(layers));
  }, [layers]);

  useEffect(() => {
    localStorage.setItem(LS_WINDOW_KEY, timeWindow);
  }, [timeWindow]);

  // ─── Toggle layer ──────────────────────────────────────────────────────────

  const toggleLayer = (key: LayerKey) => {
    setLayers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const resetFilters = () => {
    const defaults: Record<string, boolean> = {};
    LAYERS.forEach((l) => { defaults[l.key] = l.defaultOn; });
    setLayers(defaults as Record<LayerKey, boolean>);
    setTimeWindow('24h');
    setCorridor('all');
    localStorage.removeItem(LS_LAYERS_KEY);
    localStorage.removeItem(LS_WINDOW_KEY);
  };

  // ─── Corridor filter ──────────────────────────────────────────────────────

  const matchesCorridor = useCallback((ride: ActiveRide): boolean => {
    if (corridor === 'all') return true;
    const corridorDef = CORRIDORS.find((c) => c.name === corridor);
    if (!corridorDef) return true;
    const origin = (ride.origin_name || ride.origin_label || '').toLowerCase();
    const dest = (ride.destination_name || ride.destination_label || '').toLowerCase();
    return corridorDef.stops.some((s) => origin.includes(s.toLowerCase()) || dest.includes(s.toLowerCase()));
  }, [corridor]);

  // ─── Time window filter ────────────────────────────────────────────────────

  const timeWindowMs = useMemo(() => {
    return TIME_WINDOWS.find((t) => t.key === timeWindow)?.ms || 86400000;
  }, [timeWindow]);

  const isWithinTimeWindow = useCallback((dateStr: string): boolean => {
    return Date.now() - new Date(dateStr).getTime() <= timeWindowMs;
  }, [timeWindowMs]);

  // ─── Filtered data ─────────────────────────────────────────────────────────

  const filteredRides = useMemo(() => {
    return rides.filter((r) => {
      if (!layers[r.status as LayerKey]) return false;
      return matchesCorridor(r);
    });
  }, [rides, layers, matchesCorridor]);

  const [showStaleDrivers, setShowStaleDrivers] = useState(false);

  const filteredDrivers = useMemo(() => {
    if (!layers.drivers) return [];
    return onlineDrivers.filter((d) => {
      // Only include drivers with valid coordinates (plottable pins)
      if (!d.lat || !d.lng) return false;
      // Hide "Lost" drivers (staleness >= 300s) unless showStaleDrivers is checked
      if (d.staleness_seconds >= 300 && !showStaleDrivers) return false;
      return true;
    });
  }, [onlineDrivers, layers.drivers, showStaleDrivers]);

  const filteredSOS = useMemo(() => {
    if (!layers.sos) return [];
    return sosEvents.filter((e) => isWithinTimeWindow(e.created_at));
  }, [sosEvents, layers.sos, isWithinTimeWindow]);

  const filteredIncidents = useMemo(() => {
    if (!layers.incidents) return [];
    return incidents.filter((i) => isWithinTimeWindow(i.created_at));
  }, [incidents, layers.incidents, isWithinTimeWindow]);

  const filteredConcierge = useMemo(() => {
    if (!layers.concierge) return [];
    return conciergeReqs;
  }, [conciergeReqs, layers.concierge]);

  const filteredBusinesses = useMemo(() => {
    if (!layers.businesses) return [];
    return businesses;
  }, [businesses, layers.businesses]);

  // ─── Layer counts ──────────────────────────────────────────────────────────

  const layerCounts: Record<LayerKey, number> = useMemo(() => ({
    searching: filteredRides.filter((r) => r.status === 'searching').length,
    accepted: filteredRides.filter((r) => r.status === 'accepted').length,
    in_progress: filteredRides.filter((r) => r.status === 'in_progress').length,
    drivers: filteredDrivers.length,
    businesses: filteredBusinesses.length,
    sos: filteredSOS.length,
    incidents: filteredIncidents.length,
    concierge: filteredConcierge.length,
  }), [filteredRides, filteredDrivers, filteredBusinesses, filteredSOS, filteredIncidents, filteredConcierge]);

  // ─── KPI calculations (scope-aware) ───────────────────────────────────────

  const searchingCount = layerCounts.searching;
  const acceptedCount = layerCounts.accepted;
  const inProgressCount = layerCounts.in_progress;
  const driversCount = filteredDrivers.length;
  const avgFare = filteredRides.length > 0
    ? (filteredRides.reduce((sum, r) => sum + (r.fare_amount || 0), 0) / (filteredRides.filter(r => r.fare_amount).length || 1)).toFixed(0)
    : '0';

  // ─── Data fetching ─────────────────────────────────────────────────────────

  const fetchRides = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('list_active_rides_admin');
      if (error) throw error;
      const response = data as unknown as RidesResponse;
      // The RPC may also return `{ ok: false, error: '...' }` if the caller is
      // not an admin — treat that as a hard failure so it surfaces.
      if (response && (response as any).ok === false) {
        throw new Error((response as any).error || 'Rides RPC returned ok=false');
      }
      const rideList = response?.rides ?? (Array.isArray(data) ? data : []);
      setRides(rideList);
      if (ridesError) setRidesError(null);
      ridesErrorToastedRef.current = false;
    } catch (err: any) {
      const msg = err?.message || 'Unknown error';
      console.error('Failed to load rides:', msg);
      setRidesError(msg);
      // Toast once per failure streak so we don't spam on every poll/realtime
      // tick. The visible error chip in the panel keeps the operator informed.
      if (!ridesErrorToastedRef.current) {
        toast.error(`Active rides feed offline: ${msg}`, { duration: 8000 });
        ridesErrorToastedRef.current = true;
      }
    } finally {
      setLoading(false);
    }
  }, [ridesError]);

  const prevDriverPositionsRef = useRef<Map<string, { lat: number; lng: number }>>(new Map());
  const driverPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchDrivers = useCallback(async () => {
    try {
      // Try the optimized map-layer RPC first
      const { data, error } = await supabase.rpc('list_live_driver_positions_admin');
      if (error) {
        // Fallback to list_online_drivers_admin if the new RPC doesn't exist yet
        if (error.code === '42883') {
          const { data: fallbackData, error: fbErr } = await supabase.rpc('list_online_drivers_admin');
          if (fbErr) throw fbErr;
          const list = (fallbackData as any)?.drivers ?? (Array.isArray(fallbackData) ? fallbackData : []);
          setOnlineDrivers(list.filter((d: any) => d.lat && d.lng));
          return;
        }
        throw error;
      }
      const response = data as any;
      const list = response?.drivers ?? (Array.isArray(data) ? data : []);
      setOnlineDrivers(list);
    } catch (err: any) {
      console.error('Failed to load online drivers:', err.message);
    }
  }, []);

  const fetchSOS = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('sos_events')
        .select('*')
        .gte('created_at', new Date(Date.now() - 604800000).toISOString())
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      setSosEvents(data || []);
    } catch (err: any) {
      console.error('Failed to load SOS events:', err.message);
    }
  }, []);

  const fetchIncidents = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('incident_reports')
        .select('id, severity, status, subject_name, subject_role, description, created_at')
        .in('status', ['open', 'under_review'])
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      setIncidents(data || []);
    } catch (err: any) {
      console.error('Failed to load incidents:', err.message);
    }
  }, []);

  const fetchConcierge = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('hotel_concierge_requests')
        .select('*')
        .not('status', 'in', '("completed","cancelled")')
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      setConciergeReqs(data || []);
    } catch (err: any) {
      console.error('Failed to load concierge:', err.message);
    }
  }, []);

  const fetchBusinesses = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('local_business_listings')
        .select('id, name, category, is_featured, active, latitude, longitude')
        .eq('is_featured', true)
        .eq('active', true)
        .limit(200);
      if (error) throw error;
      setBusinesses(data || []);
    } catch (err: any) {
      console.error('Failed to load businesses:', err.message);
    }
  }, []);

  // ─── Initial data load ─────────────────────────────────────────────────────

  useEffect(() => {
    fetchRides();
    // 30s polling fallback for when realtime drops
    const interval = setInterval(fetchRides, 30000);
    return () => clearInterval(interval);
  }, [fetchRides]);

  // Charter KPIs — refresh on a 30s cadence and whenever any charter
  // table sees a write. We don't surface errors here (the dedicated
  // /admin/charter page is the source of truth); a silent KPI is
  // acceptable for an at-a-glance card.
  useEffect(() => {
    let cancelled = false;
    const fetchCharterKpis = async () => {
      try {
        const { data } = await supabase.rpc('admin_list_charter_requests', {
          p_status_filter: null,
          p_lookback_days: 7,
        });
        const kpis = (data as any)?.kpis;
        if (!cancelled && kpis) {
          setCharterKpis({
            open: kpis.open ?? 0,
            bidding_closed: kpis.bidding_closed ?? 0,
            assigned: kpis.assigned ?? 0,
          });
        }
      } catch (err) {
        // Silent: covered by /admin/charter feed-error toasts.
        console.warn('[LiveMap] charter KPIs fetch failed', err);
      }
    };
    fetchCharterKpis();
    const interval = setInterval(fetchCharterKpis, 30000);
    const channel = supabase
      .channel(`livemap-charter-kpis-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'charter_requests' },
        () => fetchCharterKpis(),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'charter_assignments' },
        () => fetchCharterKpis(),
      )
      .subscribe();
    return () => {
      cancelled = true;
      clearInterval(interval);
      void supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    if (layers.drivers) {
      fetchDrivers();
      // 30s polling fallback for when realtime drops
      driverPollingRef.current = setInterval(fetchDrivers, 30000);
    } else {
      if (driverPollingRef.current) {
        clearInterval(driverPollingRef.current);
        driverPollingRef.current = null;
      }
      setOnlineDrivers([]);
      prevDriverPositionsRef.current.clear();
    }
    return () => {
      if (driverPollingRef.current) {
        clearInterval(driverPollingRef.current);
        driverPollingRef.current = null;
      }
    };
  }, [layers.drivers, fetchDrivers]);

  useEffect(() => {
    if (layers.sos) fetchSOS();
  }, [layers.sos, fetchSOS]);

  useEffect(() => {
    if (layers.incidents) fetchIncidents();
  }, [layers.incidents, fetchIncidents]);

  useEffect(() => {
    if (layers.concierge) fetchConcierge();
  }, [layers.concierge, fetchConcierge]);

  useEffect(() => {
    if (layers.businesses) fetchBusinesses();
  }, [layers.businesses, fetchBusinesses]);

  // ─── Realtime subscriptions (per-layer) ────────────────────────────────────

  useEffect(() => {
    // Rides channel (always on if any ride layer is active)
    const anyRideLayer = layers.searching || layers.accepted || layers.in_progress;
    if (anyRideLayer && !channelsRef.current.has('rides')) {
      const ch = supabase
        .channel('livemap-rides')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'rides' }, () => { fetchRides(); })
        .subscribe();
      channelsRef.current.set('rides', ch);
    } else if (!anyRideLayer && channelsRef.current.has('rides')) {
      supabase.removeChannel(channelsRef.current.get('rides'));
      channelsRef.current.delete('rides');
    }

    // Drivers channel — realtime acts as change signal, RPC delivers coords
    if (layers.drivers && !channelsRef.current.has('drivers')) {
      const ch = supabase
        .channel('admin-live-drivers')
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'driver_details' }, () => {
          // Re-fetch all positions — cheap RPC, always correct
          fetchDrivers();
        })
        .subscribe();
      channelsRef.current.set('drivers', ch);
    } else if (!layers.drivers && channelsRef.current.has('drivers')) {
      supabase.removeChannel(channelsRef.current.get('drivers'));
      channelsRef.current.delete('drivers');
    }

    // SOS channel
    if (layers.sos && !channelsRef.current.has('sos')) {
      const ch = supabase
        .channel('livemap-sos')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'sos_events' }, () => { fetchSOS(); })
        .subscribe();
      channelsRef.current.set('sos', ch);
    } else if (!layers.sos && channelsRef.current.has('sos')) {
      supabase.removeChannel(channelsRef.current.get('sos'));
      channelsRef.current.delete('sos');
    }

    // Incidents channel
    if (layers.incidents && !channelsRef.current.has('incidents')) {
      const ch = supabase
        .channel('livemap-incidents')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'incident_reports' }, () => { fetchIncidents(); })
        .subscribe();
      channelsRef.current.set('incidents', ch);
    } else if (!layers.incidents && channelsRef.current.has('incidents')) {
      supabase.removeChannel(channelsRef.current.get('incidents'));
      channelsRef.current.delete('incidents');
    }

    // Concierge channel
    if (layers.concierge && !channelsRef.current.has('concierge')) {
      const ch = supabase
        .channel('livemap-concierge')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'hotel_concierge_requests' }, () => { fetchConcierge(); })
        .subscribe();
      channelsRef.current.set('concierge', ch);
    } else if (!layers.concierge && channelsRef.current.has('concierge')) {
      supabase.removeChannel(channelsRef.current.get('concierge'));
      channelsRef.current.delete('concierge');
    }

    // Listings channel
    if (layers.businesses && !channelsRef.current.has('businesses')) {
      const ch = supabase
        .channel('livemap-businesses')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'local_business_listings' }, () => { fetchBusinesses(); })
        .subscribe();
      channelsRef.current.set('businesses', ch);
    } else if (!layers.businesses && channelsRef.current.has('businesses')) {
      supabase.removeChannel(channelsRef.current.get('businesses'));
      channelsRef.current.delete('businesses');
    }
  }, [layers, fetchRides, fetchDrivers, fetchSOS, fetchIncidents, fetchConcierge, fetchBusinesses]);

  // Cleanup all channels on unmount
  useEffect(() => {
    return () => {
      channelsRef.current.forEach((ch) => supabase.removeChannel(ch));
      channelsRef.current.clear();
    };
  }, []);

  // ─── Map pin rendering ─────────────────────────────────────────────────────

  const renderAllPins = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clear old clusterer
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }

    // Clear all markers and polylines
    markersRef.current.forEach((m) => (m.map = null));
    markersRef.current.clear();
    polylinesRef.current.forEach((p) => p.setMap(null));
    polylinesRef.current.clear();

    // Track markers eligible for clustering (rides, drivers, businesses, concierge)
    const clusterableMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
    const onlineDriverIds = new Set(filteredDrivers.map((d) => d.id));
    const drawnApproaches = new Set<string>();

    // Render ride pins
    filteredRides.forEach((ride) => {
      const statusColor = ride.status === 'searching' ? '#fbbf24' : ride.status === 'accepted' ? '#3b82f6' : '#10b981';
      const statusLabel = STATUS_LABELS[ride.status] || ride.status;
      const pickup = ridePickup(ride);
      const dropoff = rideDropoff(ride);

      // Pickup pin, only while the rider is still waiting
      if (ride.status !== 'in_progress') {
        const pickupEl = document.createElement('div');
        pickupEl.style.position = 'relative';
        pickupEl.style.cursor = 'pointer';
        pickupEl.innerHTML = `
          ${ride.status === 'searching' ? `<div style="position:absolute;left:0;top:0;width:14px;height:14px;border-radius:50%;background:${statusColor};animation:searchRipple 2.4s ease-out infinite;pointer-events:none;"></div>` : ''}
          <div style="position:relative;width:14px;height:14px;background:${statusColor};border:2px solid #fff;border-radius:50%;box-shadow:0 2px 8px ${statusColor}80;"></div>
          <div class="status-pill" style="display:none;position:absolute;bottom:20px;left:50%;transform:translateX(-50%);white-space:nowrap;background:${statusColor};color:#fff;font-size:10px;font-weight:600;padding:2px 8px;border-radius:10px;box-shadow:0 2px 8px rgba(0,0,0,0.3);pointer-events:none;">${statusLabel}</div>
        `;
        pickupEl.addEventListener('mouseenter', () => {
          const pill = pickupEl.querySelector('.status-pill') as HTMLElement;
          if (pill) pill.style.display = 'block';
        });
        pickupEl.addEventListener('mouseleave', () => {
          const pill = pickupEl.querySelector('.status-pill') as HTMLElement;
          if (pill) pill.style.display = 'none';
        });
        const pickupMarker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position: pickup,
          content: pickupEl,
          title: `Pickup: ${ride.origin_label || 'Origin'}`,
        });
        pickupMarker.addListener('click', () => setSelectedPin({ type: 'ride', data: ride }));
        markersRef.current.set(`pickup-${ride.id}`, pickupMarker);
        clusterableMarkers.push(pickupMarker);
      }

      // Drop-off pin
      const dropoffEl = document.createElement('div');
      dropoffEl.style.cursor = 'pointer';
      dropoffEl.innerHTML = `<div style="width:10px;height:10px;background:#ef4444;border:2px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(239,68,68,0.4);"></div>`;
      const dropoffMarker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: dropoff,
        content: dropoffEl,
        title: `Drop-off: ${ride.destination_label || 'Destination'}`,
      });
      dropoffMarker.addListener('click', () => setSelectedPin({ type: 'ride', data: ride }));
      markersRef.current.set(`dropoff-${ride.id}`, dropoffMarker);
      clusterableMarkers.push(dropoffMarker);

      // Trip line: the road route once loaded, a dashed straight line until then
      const routeKey = roadRouteKey(pickup, dropoff);
      const roadRoutes = roadRoutesRef.current;
      if (!roadRoutes.has(routeKey)) {
        roadRoutes.set(routeKey, null);
        void fetchRoadRoute(pickup, dropoff).then((path) => {
          if (!path) return;
          roadRoutes.set(routeKey, path);
          setRoadRouteTick((t) => t + 1);
        });
      }
      const roadPath = roadRoutes.get(routeKey);
      const polyline = roadPath
        ? new google.maps.Polyline({
            path: roadPath,
            strokeColor: statusColor,
            strokeOpacity: 0.85,
            strokeWeight: 4,
            map,
          })
        : new google.maps.Polyline({
            path: [pickup, dropoff],
            geodesic: true,
            strokeColor: '#6b7280',
            strokeOpacity: 0,
            strokeWeight: 2,
            icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.6, scale: 3 }, offset: '0', repeat: '15px' }],
            map,
          });
      polylinesRef.current.set(ride.id, polyline);

      // Dashed blue road line from the bus to a rider it's on the way to collect
      if (ride.status === 'accepted' && ride.driver) {
        const online = filteredDrivers.find((d) => d.id === ride.driver!.id);
        const busPos: LatLng | null = online?.lat && online?.lng
          ? { lat: online.lat, lng: online.lng }
          : ride.driver.lat && ride.driver.lng ? { lat: ride.driver.lat, lng: ride.driver.lng } : null;
        const approachKey = `${ride.driver.id}>${pickup.lat.toFixed(5)},${pickup.lng.toFixed(5)}`;
        if (busPos && !drawnApproaches.has(approachKey)) {
          drawnApproaches.add(approachKey);
          const approaches = approachRoutesRef.current;
          const cached = approaches.get(approachKey);
          if (!cached || (!cached.pending && distanceMeters(cached.from, busPos) > APPROACH_REROUTE_METERS)) {
            approaches.set(approachKey, { from: busPos, path: cached?.path ?? null, pending: true });
            void fetchRoadRoute(busPos, pickup).then((path) => {
              approaches.set(approachKey, { from: busPos, path: path ?? cached?.path ?? null, pending: false });
              if (path) setRoadRouteTick((t) => t + 1);
            });
          }
          const approachPath = approaches.get(approachKey)?.path ?? [busPos, pickup];
          const approachCasing = new google.maps.Polyline({
            path: approachPath,
            strokeColor: '#ffffff',
            strokeOpacity: 0.9,
            strokeWeight: 7,
            map,
          });
          polylinesRef.current.set(`approach-casing-${approachKey}`, approachCasing);
          const approachLine = new google.maps.Polyline({
            path: approachPath,
            strokeColor: '#3b82f6',
            strokeOpacity: 0,
            strokeWeight: 3,
            icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.9, strokeWeight: 3, scale: 3 }, offset: '0', repeat: '12px' }],
            map,
          });
          polylinesRef.current.set(`approach-${approachKey}`, approachLine);
        }
      }

      // Bus pin from the ride, only when the online-drivers layer isn't drawing this
      // driver. One per driver: a second marker under the same key would be dropped
      // from markersRef and never cleared.
      if (
        ride.driver?.lat && ride.driver?.lng &&
        !onlineDriverIds.has(ride.driver.id) &&
        !markersRef.current.has(`ride-driver-${ride.driver.id}`)
      ) {
        // With the drivers feed on, a driver missing from it has gone offline;
        // one filtered out of it has stale GPS.
        const liveRow = onlineDrivers.find((d) => d.id === ride.driver!.id);
        const offline = layers.drivers && !liveRow;
        const driverMarker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position: { lat: ride.driver.lat, lng: ride.driver.lng },
          content: buildBusMarkerEl(
            ride.driver.full_name,
            driverTrips.get(ride.driver.id) ?? EMPTY_TRIPS,
            liveRow?.staleness_seconds ?? 0,
            offline,
          ),
          title: `Driver: ${ride.driver.full_name}${offline ? ' (offline, last known position)' : ''}`,
          zIndex: 10,
        });
        driverMarker.addListener('click', () => setSelectedPin({ type: 'ride', data: ride }));
        markersRef.current.set(`ride-driver-${ride.driver.id}`, driverMarker);
        clusterableMarkers.push(driverMarker);
      }
    });

    // Buses parked within ~35 m of each other sit side by side instead of stacked
    const sideBySideOffsetPx = new Map<string, number>();
    const spots: { pos: LatLng; ids: string[] }[] = [];
    filteredDrivers.forEach((driver) => {
      if (!driver.lat || !driver.lng) return;
      const pos = { lat: driver.lat, lng: driver.lng };
      const spot = spots.find((s) => distanceMeters(s.pos, pos) < 35);
      if (spot) spot.ids.push(driver.id);
      else spots.push({ pos, ids: [driver.id] });
    });
    spots.forEach((spot) => {
      spot.ids.forEach((id, i) => sideBySideOffsetPx.set(id, (i - (spot.ids.length - 1) / 2) * 120));
    });

    // One bus pin per online driver, named, with what they're doing right now
    filteredDrivers.forEach((driver) => {
      if (!driver.lat || !driver.lng) return;

      const staleness = driver.staleness_seconds || 0;
      const trips = driverTrips.get(driver.id) ?? EMPTY_TRIPS;
      const stateLabel = staleness >= 60 ? `GPS ${staleness}s ago` : tripSummary(trips);
      const el = buildBusMarkerEl(driver.full_name, trips, staleness);
      const offsetPx = sideBySideOffsetPx.get(driver.id) ?? 0;
      if (offsetPx) el.style.transform = `translateX(${offsetPx}px)`;

      // Check if we have a previous position for smooth animation
      const prevPos = prevDriverPositionsRef.current.get(driver.id);
      const startPos = prevPos || { lat: driver.lat, lng: driver.lng };

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: startPos,
        content: el,
        title: `${driver.full_name}${driver.license_plate ? ` (${driver.license_plate})` : ''}: ${stateLabel}`,
        zIndex: 10,
      });
      marker.addListener('click', () => setSelectedPin({ type: 'driver', data: driver }));
      markersRef.current.set(`driver-${driver.id}`, marker);
      clusterableMarkers.push(marker);

      // Smooth movement animation (~800ms easeOutQuad)
      if (prevPos && (prevPos.lat !== driver.lat || prevPos.lng !== driver.lng)) {
        const duration = 800;
        const startTime = performance.now();
        const deltaLat = driver.lat - prevPos.lat;
        const deltaLng = driver.lng - prevPos.lng;
        const easeOutQuad = (t: number) => t * (2 - t);

        const animate = (now: number) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = easeOutQuad(progress);
          const newLat = prevPos.lat + deltaLat * eased;
          const newLng = prevPos.lng + deltaLng * eased;
          marker.position = { lat: newLat, lng: newLng };
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      }

      // Store current position for next animation
      prevDriverPositionsRef.current.set(driver.id, { lat: driver.lat, lng: driver.lng });
    });

    // Render SOS pins (always individual, no clustering)
    filteredSOS.forEach((sos) => {
      const lat = sos.lat || 15.4150;
      const lng = sos.lng || -61.3710;
      const isRecent = Date.now() - new Date(sos.created_at).getTime() < 600000; // < 10 min
      const el = document.createElement('div');
      el.style.cursor = 'pointer';
      el.innerHTML = `<div style="width:20px;height:20px;background:#ef4444;border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 10px rgba(239,68,68,0.6);${isRecent ? 'animation:pulse 1.5s infinite;' : ''}"><svg width="10" height="10" viewBox="0 0 24 24" fill="white" stroke="none"><path d="M12 2L2 22h20L12 2zm0 4l7.5 14h-15L12 6z"/></svg></div>`;
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat, lng },
        content: el,
        title: `SOS: ${sos.user_role} - ${timeAgo(sos.created_at)} ago`,
      });
      marker.addListener('click', () => setSelectedPin({ type: 'sos', data: sos }));
      markersRef.current.set(`sos-${sos.id}`, marker);
    });

    // Render incident pins (always individual, no clustering).
    // Only plot pins that have real GPS coordinates — random jitter
    // is misleading to operators investigating on-the-ground incidents.
    filteredIncidents.forEach((inc) => {
      if (!inc.lat || !inc.lng) return;
      const lat = inc.lat;
      const lng = inc.lng;
      const el = document.createElement('div');
      el.style.cursor = 'pointer';
      el.innerHTML = `<div style="width:20px;height:20px;background:#f59e0b;border:2px solid #fff;border-radius:4px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(245,158,11,0.5);"><svg width="10" height="10" viewBox="0 0 24 24" fill="white" stroke="none"><path d="M14.4 6l.3 1.5H19v7h-4.8l-.3-1.5H7V6h7.4M15 4H5v16h2v-6h5.6l.3 1.5H21V5.5h-5.7L15 4z"/></svg></div>`;
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat, lng },
        content: el,
        title: `Incident: ${inc.severity} - ${inc.subject_name}`,
      });
      marker.addListener('click', () => setSelectedPin({ type: 'incident', data: inc }));
      markersRef.current.set(`incident-${inc.id}`, marker);
    });

    // Render concierge pins — only when real pickup coords are available.
    filteredConcierge.forEach((req) => {
      if (!req.pickup_lat || !req.pickup_lng) return;
      const lat = req.pickup_lat;
      const lng = req.pickup_lng;
      const el = document.createElement('div');
      el.style.cursor = 'pointer';
      el.innerHTML = `<div style="width:20px;height:20px;background:#8b5cf6;border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(139,92,246,0.5);"><svg width="10" height="10" viewBox="0 0 24 24" fill="white" stroke="none"><path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V6H1v15h2v-3h18v3h2V11c0-2.21-1.79-4-4-4z"/></svg></div>`;
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat, lng },
        content: el,
        title: `Concierge: ${req.guest_name} at ${req.hotel_name}`,
      });
      marker.addListener('click', () => setSelectedPin({ type: 'concierge', data: req }));
      markersRef.current.set(`concierge-${req.id}`, marker);
      clusterableMarkers.push(marker);
    });

    // Render business pins
    filteredBusinesses.forEach((biz) => {
      if (!biz.latitude || !biz.longitude) return;
      const el = document.createElement('div');
      el.style.cursor = 'pointer';
      el.innerHTML = `<div style="width:20px;height:20px;background:#f97316;border:2px solid #fff;border-radius:4px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(249,115,22,0.5);"><svg width="10" height="10" viewBox="0 0 24 24" fill="white" stroke="none"><path d="M20 4H4v2h16V4zm1 10v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6h1zm-9 4H6v-4h6v4z"/></svg></div>`;
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat: biz.latitude, lng: biz.longitude },
        content: el,
        title: `Business: ${biz.name}`,
      });
      marker.addListener('click', () => setSelectedPin({ type: 'business', data: biz }));
      markersRef.current.set(`biz-${biz.id}`, marker);
      clusterableMarkers.push(marker);
    });

    // Apply clustering when clusterable markers exceed 40
    if (clusterableMarkers.length > 40) {
      // Remove individual map assignment for clusterable markers — clusterer will manage them
      clusterableMarkers.forEach((m) => (m.map = null));
      clustererRef.current = new MarkerClusterer({
        map,
        markers: clusterableMarkers,
        renderer: {
          render: ({ count, position }) => {
            const el = document.createElement('div');
            el.innerHTML = `<div style="width:36px;height:36px;background:rgba(16,185,129,0.85);border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 3px 12px rgba(0,0,0,0.4);font-size:12px;font-weight:700;color:#fff;">${count}</div>`;
            return new google.maps.marker.AdvancedMarkerElement({
              position,
              content: el,
            });
          },
        },
      });
    }
  }, [driverTrips, onlineDrivers, layers.drivers, filteredRides, filteredDrivers, filteredSOS, filteredIncidents, filteredConcierge, filteredBusinesses, roadRouteTick]);

  // Re-render pins when filtered data changes
  useEffect(() => {
    if (mapRef.current) renderAllPins();
  }, [renderAllPins]);

  // ─── Map ready handler ─────────────────────────────────────────────────────

  const handleMapReady = (map: google.maps.Map) => {
    mapRef.current = map;
    map.setOptions({ styles: DARK_MAP_STYLES });
    renderAllPins();
  };

  // ─── Center on ride ────────────────────────────────────────────────────────

  /** Where the driver is now: the live GPS feed first, then the ride's copy. */
  const driverPosition = (driver: RideDriver): LatLng | null => {
    const online = onlineDrivers.find((d) => d.id === driver.id);
    if (online?.lat && online?.lng) return { lat: online.lat, lng: online.lng };
    return driver.lat && driver.lng ? { lat: driver.lat, lng: driver.lng } : null;
  };

  const centerOnRide = (ride: ActiveRide) => {
    setSelectedPin({ type: 'ride', data: ride });
    if (mapRef.current) {
      const busPos = ride.status === 'in_progress' && ride.driver ? driverPosition(ride.driver) : null;
      mapRef.current.panTo(busPos ?? ridePickup(ride));
      mapRef.current.setZoom(14);
    }
  };

  const focusDriver = (driver: RideDriver, fallbackRide: ActiveRide) => {
    const online = onlineDrivers.find((d) => d.id === driver.id);
    if (!online) {
      centerOnRide(fallbackRide);
      return;
    }
    setSelectedPin({ type: 'driver', data: online });
    const pos = driverPosition(driver);
    if (pos && mapRef.current) {
      mapRef.current.panTo(pos);
      mapRef.current.setZoom(14);
    }
  };

  // ─── Total visible pin count ───────────────────────────────────────────────

  const totalPinCount = filteredRides.length + filteredDrivers.length + filteredSOS.length + filteredIncidents.length + filteredConcierge.length + filteredBusinesses.length;
  const activeFilterCount = Object.values(layers).filter(Boolean).length + (corridor !== 'all' ? 1 : 0);

  // ─── Time window enabled ──────────────────────────────────────────────────

  const timeWindowEnabled = layers.sos || layers.incidents;

  // ─── Sorted rides for panel ────────────────────────────────────────────────

  const sortedRides = [...filteredRides].sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9));

  // Riders still waiting for a driver first, then one group per driver
  const rideGroups: { key: string; driver: RideDriver | null; rides: ActiveRide[] }[] = [];
  sortedRides.forEach((ride) => {
    const key = ride.driver?.id ?? 'unassigned';
    let group = rideGroups.find((g) => g.key === key);
    if (!group) {
      group = { key, driver: ride.driver, rides: [] };
      rideGroups.push(group);
    }
    group.rides.push(ride);
  });
  rideGroups.sort((a, b) =>
    a.driver && b.driver ? a.driver.full_name.localeCompare(b.driver.full_name) : a.driver ? 1 : b.driver ? -1 : 0,
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="h-[calc(100vh-3.5rem)] -m-6 flex flex-col">
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.7; }
        }
        @keyframes searchRipple {
          0% { transform: scale(1); opacity: 0.55; }
          100% { transform: scale(2.8); opacity: 0; }
        }
      `}</style>

      {/* KPI Strip — scope-aware */}
      <div className="flex items-center gap-4 px-6 py-3 bg-[#111827] border-b border-gray-800 shrink-0">
        <div className="flex items-center gap-6 flex-1">
          <KPICard label="Searching Now" value={searchingCount} color="text-amber-400" pulse={searchingCount > 0} />
          <KPICard label="Accepted" value={acceptedCount} color="text-blue-400" />
          <KPICard label="In Progress" value={inProgressCount} color="text-emerald-400" />
          <KPICard label="Online Drivers" value={driversCount} color="text-green-400" />
          <KPICard label="Avg Fare (EC$)" value={`$${avgFare}`} color="text-white" />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-gray-400 hover:text-white"
          onClick={() => setPanelOpen(!panelOpen)}
        >
          {panelOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
        </Button>
      </div>

      {/* Main content: Map + Side Panel */}
      <div className="flex-1 flex overflow-hidden">
        {/* Map area */}
        <div className="flex-1 relative">
          {loading && (
            <div className="absolute inset-0 z-10 bg-[#0b0f17] flex items-center justify-center">
              <div className="space-y-3 text-center">
                <Skeleton className="h-8 w-48 bg-gray-800 mx-auto" />
                <p className="text-sm text-gray-500">Loading live map...</p>
              </div>
            </div>
          )}

          <MapView
            className="w-full h-full"
            initialCenter={{ lat: 15.4150, lng: -61.3710 }}
            initialZoom={10}
            onMapReady={handleMapReady}
          />

          {/* Filter panel overlay — top-left */}
          <div className={`absolute top-3 left-3 z-20 transition-all duration-200 ${filterPanelOpen ? 'w-[240px]' : 'w-[40px]'}`}>
            {filterPanelOpen ? (
              <div className="bg-[#111827]/95 backdrop-blur-sm border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-700/50">
                  <span className="text-xs font-semibold text-white uppercase tracking-wider">Map Filters</span>
                  <button onClick={() => setFilterPanelOpen(false)} className="text-gray-400 hover:text-white transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                </div>

                {/* Layers */}
                <div className="px-3 py-2.5 space-y-1.5 max-h-[280px] overflow-y-auto">
                  {LAYERS.map((layer) => {
                    const Icon = LAYER_ICONS[layer.key];
                    const isOn = layers[layer.key];
                    const count = layerCounts[layer.key];
                    return (
                      <button
                        key={layer.key}
                        onClick={() => toggleLayer(layer.key)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all ${
                          isOn
                            ? 'bg-gray-700/50 text-white'
                            : 'text-gray-500 hover:bg-gray-800/50 hover:text-gray-300'
                        }`}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0 transition-opacity"
                          style={{ backgroundColor: layer.color, opacity: isOn ? 1 : 0.3 }}
                        />
                        <span className="flex-1 text-left truncate">{layer.label}</span>
                        {isOn && <span className="text-[10px] font-mono text-gray-400">{count}</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Corridor slicer */}
                <div className="px-3 py-2.5 border-t border-gray-700/50">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Corridor</p>
                  <select
                    value={corridor}
                    onChange={(e) => setCorridor(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-emerald-500/50"
                  >
                    <option value="all">All corridors</option>
                    {CORRIDORS.map((c) => (
                      <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>

                {/* Time window */}
                <div className="px-3 py-2.5 border-t border-gray-700/50">
                  <p className={`text-[10px] uppercase tracking-wider mb-1.5 ${timeWindowEnabled ? 'text-gray-500' : 'text-gray-600'}`}>
                    Time Window {!timeWindowEnabled && <span className="text-gray-700">(enable SOS/Incidents)</span>}
                  </p>
                  <div className="flex gap-1">
                    {TIME_WINDOWS.map((tw) => (
                      <button
                        key={tw.key}
                        disabled={!timeWindowEnabled}
                        onClick={() => setTimeWindow(tw.key)}
                        className={`flex-1 py-1 rounded text-[10px] font-medium transition-all ${
                          !timeWindowEnabled
                            ? 'bg-gray-800/30 text-gray-700 cursor-not-allowed'
                            : timeWindow === tw.key
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                              : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50 border border-transparent'
                        }`}
                      >
                        {tw.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Show stale drivers */}
                {layers.drivers && (
                  <div className="px-3 py-2.5 border-t border-gray-700/50">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showStaleDrivers}
                        onChange={(e) => setShowStaleDrivers(e.target.checked)}
                        className="w-3.5 h-3.5 rounded border-gray-600 bg-gray-800 text-emerald-500 focus:ring-emerald-500/50 focus:ring-offset-0"
                      />
                      <span className="text-[10px] text-gray-400">Show stale drivers (&gt;5min)</span>
                    </label>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setFilterPanelOpen(true)}
                className="bg-[#111827]/95 backdrop-blur-sm border border-gray-700 rounded-xl shadow-2xl w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                title="Open filters"
              >
                <div className="relative">
                  <ChevronRight className="w-4 h-4" />
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-emerald-500 rounded-full text-[8px] text-white flex items-center justify-center font-bold">
                      {activeFilterCount}
                    </span>
                  )}
                </div>
              </button>
            )}
          </div>

          {/* Legend */}
          <div className="absolute bottom-8 left-3 z-10 pointer-events-none bg-[#111827]/90 border border-gray-700 rounded-lg px-3 py-2 space-y-1 text-[10px] text-gray-300">
            <div className="flex items-center gap-2">
              <Bus className="w-3 h-3 text-emerald-400 shrink-0" />
              <span>Bus: <span className="text-emerald-400">green</span> riders on board, <span className="text-blue-400">blue</span> going to pick up, <span className="text-gray-400">gray</span> free, <span className="text-amber-400">orange</span> or faded: no recent GPS</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400 border border-white shrink-0" />
              <span>Rider waiting (yellow: no driver yet, blue: driver on the way)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 shrink-0 border-t-2 border-dashed border-blue-500" />
              <span>Bus driving to pick up</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 border border-white shrink-0" />
              <span>Drop-off spot</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-1 rounded bg-emerald-500 shrink-0" />
              <span>Trip route</span>
            </div>
          </div>

          {/* Empty state overlay */}
          {!loading && totalPinCount === 0 && (
            <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
              <div className="bg-[#111827]/95 backdrop-blur-sm border border-gray-700 rounded-2xl shadow-2xl p-8 text-center max-w-sm pointer-events-auto">
                <MapPin className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <h3 className="text-white font-semibold text-lg mb-2">Quiet on the corridors.</h3>
                <p className="text-sm text-gray-400 mb-4">
                  Nothing matches your current filter. Try expanding your time window or turning on more layers.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white"
                  onClick={resetFilters}
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset filters
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Right side panel */}
        {panelOpen && (
          <aside className="w-[340px] bg-[#111827] border-l border-gray-800 flex flex-col overflow-hidden shrink-0">
            {/* Detail card or ride list */}
            {selectedPin ? (
              <div className="flex-1 overflow-y-auto">
                <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-white">
                    {selectedPin.type === 'ride' ? 'Ride Details' :
                     selectedPin.type === 'driver' ? 'Driver Details' :
                     selectedPin.type === 'sos' ? 'SOS Event' :
                     selectedPin.type === 'incident' ? 'Incident Report' :
                     selectedPin.type === 'concierge' ? 'Concierge Request' :
                     'Business Listing'}
                  </h3>
                  <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white h-7 w-7 p-0" onClick={() => setSelectedPin(null)}>
                    ✕
                  </Button>
                </div>
                <div className="p-4">
                  <PinDetailCard
                    pin={liveSelectedPin ?? selectedPin}
                    rideEnded={selectedRideEnded}
                    driverTrips={driverTrips}
                    onSelectRide={centerOnRide}
                  />
                </div>
              </div>
            ) : (
              <>
                {/*
                  Charter activity at-a-glance. Sealed-bid private
                  charter activity flows through the dedicated
                  /admin/charter page; this card is just a
                  monitoring tile so the operator can see if there's
                  anything happening in the charter marketplace
                  without leaving the corridor map.
                */}
                {charterKpis && (charterKpis.open > 0 || charterKpis.bidding_closed > 0 || charterKpis.assigned > 0) ? (
                  <Link href="/admin/charter">
                    <a className="block px-4 py-3 border-b border-gray-800 hover:bg-emerald-500/5 transition-colors">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-1.5">
                          <Bus className="w-3.5 h-3.5 text-emerald-400" />
                          <h3 className="text-sm font-semibold text-white">Charter activity</h3>
                        </div>
                        <span className="text-[10px] text-emerald-300 font-medium">Open dashboard →</span>
                      </div>
                      <p className="text-xs text-gray-400">
                        {charterKpis.open > 0 && (
                          <span className="text-emerald-300 font-semibold">
                            {charterKpis.open} live bid{charterKpis.open === 1 ? '' : 's'}
                          </span>
                        )}
                        {charterKpis.open > 0 && (charterKpis.bidding_closed > 0 || charterKpis.assigned > 0) ? ' · ' : ''}
                        {charterKpis.bidding_closed > 0 && (
                          <span className="text-amber-300">
                            {charterKpis.bidding_closed} awaiting pick
                          </span>
                        )}
                        {charterKpis.bidding_closed > 0 && charterKpis.assigned > 0 ? ' · ' : ''}
                        {charterKpis.assigned > 0 && (
                          <span className="text-blue-300">
                            {charterKpis.assigned} assigned
                          </span>
                        )}
                      </p>
                    </a>
                  </Link>
                ) : null}

                <div className="px-4 py-3 border-b border-gray-800">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-white">Active Rides</h3>
                    {ridesError && (
                      <button
                        onClick={() => fetchRides()}
                        className="flex items-center gap-1 text-[10px] font-medium text-red-300 bg-red-500/15 border border-red-500/30 rounded px-1.5 py-0.5 hover:bg-red-500/25 transition-colors"
                        title={`Rides RPC error: ${ridesError}\nClick to retry`}
                      >
                        <AlertTriangle className="w-3 h-3" />
                        Feed error. Retry
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {ridesError
                      ? 'Live rides offline. See the error above.'
                      : `${filteredRides.length} ride${filteredRides.length !== 1 ? 's' : ''} on map`}
                  </p>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {sortedRides.length === 0 ? (
                    <div className="p-8 text-center">
                      <MapPin className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No active rides</p>
                    </div>
                  ) : (
                    <div>
                      {rideGroups.map((group) => {
                        const groupDriver = group.driver;
                        const trips = groupDriver ? driverTrips.get(groupDriver.id) ?? EMPTY_TRIPS : EMPTY_TRIPS;
                        return (
                    <div key={group.key} className="border-b border-gray-800">
                      <button
                        type="button"
                        className="w-full px-4 py-2 bg-gray-800/50 hover:bg-gray-800 flex items-center gap-2 text-left transition-colors"
                        onClick={() => (groupDriver ? focusDriver(groupDriver, group.rides[0]) : centerOnRide(group.rides[0]))}
                        aria-label={groupDriver ? `Show ${groupDriver.full_name} on the map` : 'Show waiting riders on the map'}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: groupDriver ? tripColor(trips) : '#fbbf24' }}
                        />
                        <span className="text-xs font-semibold text-white truncate">
                          {groupDriver ? groupDriver.full_name : 'Waiting for a driver'}
                        </span>
                        {groupDriver?.vehicle_plate && (
                          <span className="text-[10px] text-gray-500 font-mono">{groupDriver.vehicle_plate}</span>
                        )}
                        <span className="ml-auto text-[10px] text-gray-400 shrink-0">
                          {groupDriver ? tripSummary(trips) : `${group.rides.length} searching`}
                        </span>
                      </button>
                    <div className="divide-y divide-gray-800/60">
                      {group.rides.map((ride) => {
                        const statusStyle = STATUS_COLORS[ride.status] || STATUS_COLORS.searching;
                        return (
                          <div
                            key={ride.id}
                            className="px-4 py-3 cursor-pointer transition-colors hover:bg-gray-800/30"
                            onClick={() => centerOnRide(ride)}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <div className="flex items-center gap-2 min-w-0">
                                {ride.passenger_profile_picture_url ? (
                                  <img src={ride.passenger_profile_picture_url} alt="" className="w-5 h-5 rounded-full object-cover bg-gray-700 shrink-0" referrerPolicy="no-referrer" />
                                ) : null}
                                <span className="text-sm font-medium text-white truncate">
                                  {ride.passenger_name || 'Passenger'}
                                </span>
                              </div>
                              <Badge className={`${statusStyle.bg} ${statusStyle.text} border-0 text-[10px] px-1.5 py-0 shrink-0`}>
                                {STATUS_LABELS[ride.status]}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
                              <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                              <span className="truncate">{ride.origin_label || 'Pickup'}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-2">
                              <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                              <span className="truncate">{ride.destination_label || 'Drop-off'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              {ride.fare_amount != null && (
                                <span className="text-xs font-semibold text-white">EC${ride.fare_amount}</span>
                              )}
                              <div className="flex items-center gap-1 text-[10px] text-gray-500 ml-auto">
                                <Clock className="w-3 h-3" />
                                {timeAgo(ride.requested_at)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}

// ─── Pin Detail Card ─────────────────────────────────────────────────────────

function PinDetailCard({ pin, rideEnded, driverTrips, onSelectRide }: {
  pin: PinDetail;
  rideEnded: boolean;
  driverTrips: Map<string, DriverTrips>;
  onSelectRide: (ride: ActiveRide) => void;
}) {
  switch (pin.type) {
    case 'ride': return <RideDetailCard ride={pin.data} ended={rideEnded} />;
    case 'driver': return <DriverDetailCard driver={pin.data} trips={driverTrips.get(pin.data.id) ?? EMPTY_TRIPS} onSelectRide={onSelectRide} />;
    case 'sos': return <SOSDetailCard sos={pin.data} />;
    case 'incident': return <IncidentDetailCard incident={pin.data} />;
    case 'concierge': return <ConciergeDetailCard req={pin.data} />;
    case 'business': return <BusinessDetailCard biz={pin.data} />;
  }
}

function RideDetailCard({ ride, ended }: { ride: ActiveRide; ended: boolean }) {
  const statusStyle = STATUS_COLORS[ride.status] || STATUS_COLORS.searching;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {ended ? (
          <Badge className="bg-gray-700 text-gray-300 border-0 text-xs px-2 py-0.5">Trip ended</Badge>
        ) : (
          <Badge className={`${statusStyle.bg} ${statusStyle.text} border-0 text-xs px-2 py-0.5`}>
            {STATUS_LABELS[ride.status]}
          </Badge>
        )}
      </div>
      {ended && (
        <p className="text-xs text-gray-400">
          This trip is no longer active. The rider was dropped off or the ride was canceled. Ride History has the final record.
        </p>
      )}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          {ride.passenger_profile_picture_url ? (
            <img src={ride.passenger_profile_picture_url} alt={ride.passenger_name || 'Passenger'} className="w-6 h-6 rounded-full object-cover bg-gray-700 shrink-0" referrerPolicy="no-referrer" />
          ) : (
            <Users className="w-4 h-4 text-gray-400" />
          )}
          <span className="text-sm text-white">{ride.passenger_name || 'Unknown passenger'}</span>
        </div>
        {ride.on_behalf_of_name && (
          <BookingForChip
            name={ride.on_behalf_of_name}
            ageBand={ride.on_behalf_of_age_band}
            notes={ride.on_behalf_of_notes}
          />
        )}
        {ride.driver ? (
          <div className="flex items-center gap-2">
            {ride.driver.profile_picture_url ? (
              <img src={ride.driver.profile_picture_url} alt={ride.driver.full_name} className="w-6 h-6 rounded-full object-cover bg-gray-700 shrink-0" referrerPolicy="no-referrer" />
            ) : (
              <Car className="w-4 h-4 text-blue-400" />
            )}
            <span className="text-sm text-blue-300">{ride.driver.full_name}</span>
            {ride.driver.vehicle_plate && (
              <span className="text-[10px] text-gray-500 font-mono bg-gray-800 px-1.5 py-0.5 rounded">{ride.driver.vehicle_plate}</span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Car className="w-4 h-4 text-gray-600" />
            <span className="text-sm text-gray-500 italic">Unassigned</span>
          </div>
        )}
      </div>
      {/* Contact info */}
      <div className="space-y-0.5">
        <ContactRow label="Passenger phone" value={formatDominicaPhone(ride.passenger_phone)} callable />
        {ride.driver && (
          <>
            <ContactRow label="Driver phone" value={formatDominicaPhone(ride.driver.phone_number)} callable />
            <ContactRow label="Plate" value={ride.driver.vehicle_plate} />
          </>
        )}
      </div>
      <div className="bg-gray-800/50 rounded-lg p-3 space-y-1.5">
        <div className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-gray-300">{ride.origin_label || 'Pickup'}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-gray-300">{ride.destination_label || 'Drop-off'}</span>
        </div>
        {ride.dropoff_is_custom && (
          <div className="pl-4 text-[11px] text-amber-300">
            Rider moved the drop-off pin{ride.dropoff_notes ? `: ${ride.dropoff_notes}` : ''}
          </div>
        )}
      </div>
      {ride.fare_amount != null && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Fare</span>
          <span className="text-sm font-semibold text-white">EC${Number(ride.fare_amount).toFixed(2)}</span>
        </div>
      )}
      <div className="space-y-1 text-xs text-gray-400">
        <div className="flex justify-between">
          <span>Requested</span>
          <span className="text-gray-300">{timeAgo(ride.requested_at)} ago</span>
        </div>
        {ride.accepted_at && (
          <div className="flex justify-between">
            <span>Accepted</span>
            <span className="text-gray-300">{timeAgo(ride.accepted_at)} ago</span>
          </div>
        )}
        {ride.picked_up_at && (
          <div className="flex justify-between">
            <span>Picked up</span>
            <span className="text-gray-300">{timeAgo(ride.picked_up_at)} ago</span>
          </div>
        )}
      </div>
    </div>
  );
}

function DriverDetailCard({ driver, trips, onSelectRide }: {
  driver: OnlineDriver;
  trips: DriverTrips;
  onSelectRide: (ride: ActiveRide) => void;
}) {
  const staleness = driver.staleness_seconds || 0;
  let stateLabel = 'Idle';
  let stateColor = 'text-emerald-400';
  if (driver.is_busy && staleness < 60) {
    stateLabel = 'In ride';
    stateColor = 'text-sky-400';
  } else if (staleness >= 300) {
    stateLabel = 'Lost signal';
    stateColor = 'text-slate-400';
  } else if (staleness >= 60) {
    stateLabel = 'Stale GPS';
    stateColor = 'text-amber-400';
  }

  const formatStaleness = (s: number) => {
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s ago`;
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m ago`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        {(driver.profile_picture_url || driver.avatar_url) ? (
          <img
            src={driver.profile_picture_url || driver.avatar_url || ''}
            alt={driver.full_name}
            className="w-10 h-10 rounded-full object-cover bg-gray-700 shrink-0"
            referrerPolicy="no-referrer"
            onError={(e) => {
              const img = e.currentTarget;
              img.style.display = 'none';
              const fb = document.createElement('div');
              fb.className = 'w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold text-sm shrink-0';
              fb.textContent = driver.full_name?.slice(0, 2).toUpperCase() || '??';
              img.parentNode?.insertBefore(fb, img.nextSibling);
            }}
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
            {driver.full_name?.slice(0, 2).toUpperCase() || '??'}
          </div>
        )}
        <div>
          <p className="text-sm font-medium text-white">{driver.full_name}</p>
          {driver.license_plate && (
            <p className="text-[10px] text-gray-500 font-mono">{driver.license_plate}</p>
          )}
        </div>
      </div>

      {/* Contact */}
      <div className="space-y-0.5">
        <ContactRow label="Phone" value={formatDominicaPhone(driver.phone_number)} callable />
        <ContactRow label="Plate" value={driver.license_plate} />
      </div>

      {/* Vehicle & Corridor */}
      <div className="bg-gray-800/50 rounded-lg p-3 space-y-2 text-xs">
        {(driver.vehicle_make || driver.vehicle_model) && (
          <div className="flex justify-between">
            <span className="text-gray-500">Vehicle</span>
            <span className="text-gray-300">{[driver.vehicle_make, driver.vehicle_model].filter(Boolean).join(' ')}</span>
          </div>
        )}
        {driver.corridor_id && (
          <div className="flex justify-between">
            <span className="text-gray-500">Corridor</span>
            <span className="text-gray-300">{driver.corridor_id}</span>
          </div>
        )}
        {driver.current_direction && (
          <div className="flex justify-between">
            <span className="text-gray-500">Direction</span>
            <span className="text-gray-300 capitalize">{driver.current_direction}</span>
          </div>
        )}
        {(driver.current_route_origin || driver.current_route_destination) && (
          <div className="flex justify-between">
            <span className="text-gray-500">Route</span>
            <span className="text-gray-300">{driver.current_route_origin || '?'} → {driver.current_route_destination || '?'}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-500">Seats</span>
          <span className="text-gray-300">{driver.seats_available}</span>
        </div>
      </div>

      {/* Status */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500">Status</span>
        <span className={`font-medium ${stateColor}`}>{stateLabel}</span>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500">Last GPS update</span>
        <span className="text-gray-300">{formatStaleness(staleness)}</span>
      </div>

      {/* Riders */}
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-wider text-gray-500">Riders</p>
        {trips.onBoard.length + trips.toPickUp.length === 0 ? (
          <p className="text-xs text-gray-400">No riders right now.</p>
        ) : (
          <div className="space-y-1">
            {[...trips.onBoard, ...trips.toPickUp].map((ride) => (
              <button
                key={ride.id}
                type="button"
                onClick={() => onSelectRide(ride)}
                className="w-full flex items-center gap-2 rounded-lg bg-gray-800/50 hover:bg-gray-800 px-3 py-2 text-left transition-colors"
                aria-label={`Open ${ride.passenger_name || 'passenger'}'s ride`}
              >
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: ride.status === 'in_progress' ? '#10b981' : '#3b82f6' }}
                />
                <span className="text-xs text-white truncate">{ride.passenger_name || 'Passenger'}</span>
                <span className="ml-auto text-[10px] text-gray-400 shrink-0">
                  {ride.status === 'in_progress'
                    ? `On board → ${ride.destination_label || ride.destination_name || 'drop-off'}`
                    : `Pick up at ${ride.origin_label || ride.origin_name || 'stop'}`}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="pt-2 border-t border-gray-700 space-y-2">
        <Button
          variant="outline"
          size="sm"
          className="w-full border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-white text-xs"
          onClick={() => window.location.href = '/admin/drivers'}
        >
          Open in Drivers tab
        </Button>
      </div>
    </div>
  );
}

function SOSDetailCard({ sos }: { sos: SOSEvent }) {
  const isRecent = Date.now() - new Date(sos.created_at).getTime() < 600000;
  return (
    <div className="space-y-4">
      {isRecent && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
          </span>
          <span className="text-xs text-red-400 font-medium">ACTIVE SOS: {timeAgo(sos.created_at)} ago</span>
        </div>
      )}
      <div className="space-y-2 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-500">Time pressed</span>
          <span className="text-gray-300">{timeAgo(sos.created_at)} ago</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">User role</span>
          <span className="text-gray-300 capitalize">{sos.user_role}</span>
        </div>
        {sos.user_name && (
          <div className="flex justify-between">
            <span className="text-gray-500">User</span>
            <span className="text-gray-300">{sos.user_name}</span>
          </div>
        )}
        {sos.ride_id && (
          <div className="flex justify-between">
            <span className="text-gray-500">Ride</span>
            <span className="text-gray-300 font-mono text-[10px]">{sos.ride_id.slice(0, 12)}...</span>
          </div>
        )}
      </div>
      <div className="pt-2 border-t border-gray-700">
        <Button
          variant="outline"
          size="sm"
          className="w-full border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-white text-xs"
          onClick={() => window.location.href = '/admin/incidents'}
        >
          Open linked incident report
        </Button>
      </div>
    </div>
  );
}

function IncidentDetailCard({ incident }: { incident: IncidentPin }) {
  const severityColors: Record<string, string> = {
    critical: 'bg-red-500/20 text-red-400',
    high: 'bg-orange-500/20 text-orange-400',
    medium: 'bg-amber-500/20 text-amber-400',
    low: 'bg-gray-500/20 text-gray-400',
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Badge className={`${severityColors[incident.severity] || severityColors.medium} border-0 text-xs px-2 py-0.5`}>
          {incident.severity}
        </Badge>
        <Badge className="bg-gray-700/50 text-gray-300 border-0 text-xs px-2 py-0.5">
          {incident.status.replace('_', ' ')}
        </Badge>
      </div>
      <div className="space-y-2 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-500">Subject</span>
          <span className="text-gray-300">{incident.subject_name}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Role</span>
          <span className="text-gray-300 capitalize">{incident.subject_role}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Reported</span>
          <span className="text-gray-300">{timeAgo(incident.created_at)} ago</span>
        </div>
      </div>
      {incident.description && (
        <div className="bg-gray-800/50 rounded-lg p-3">
          <p className="text-xs text-gray-300 line-clamp-3">{incident.description}</p>
        </div>
      )}
      <div className="pt-2 border-t border-gray-700">
        <Button
          variant="outline"
          size="sm"
          className="w-full border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-white text-xs"
          onClick={() => window.location.href = '/admin/incidents'}
        >
          Open report
        </Button>
      </div>
    </div>
  );
}

function ConciergeDetailCard({ req }: { req: ConciergePin }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Hotel className="w-4 h-4 text-purple-400" />
        <span className="text-sm text-white font-medium">{req.guest_name}</span>
      </div>
      <div className="space-y-2 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-500">Hotel</span>
          <span className="text-gray-300">{req.hotel_name}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Status</span>
          <span className="text-gray-300 capitalize">{req.status?.replace('_', ' ')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Requested</span>
          <span className="text-gray-300">{timeAgo(req.created_at)} ago</span>
        </div>
      </div>
    </div>
  );
}

function BusinessDetailCard({ biz }: { biz: BusinessPin }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Store className="w-4 h-4 text-orange-400" />
        <span className="text-sm text-white font-medium">{biz.name}</span>
      </div>
      <div className="space-y-2 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-500">Category</span>
          <span className="text-gray-300 capitalize">{biz.category}</span>
        </div>
        <div className="flex items-center gap-2">
          {biz.is_featured && <Badge className="bg-orange-500/20 text-orange-400 border-0 text-[10px] px-1.5 py-0">Featured</Badge>}
          {biz.active && <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-[10px] px-1.5 py-0">Active</Badge>}
        </div>
      </div>
      <div className="pt-2 border-t border-gray-700">
        <Button
          variant="outline"
          size="sm"
          className="w-full border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-white text-xs"
          onClick={() => window.location.href = '/admin/listings'}
        >
          Edit in Listings tab
        </Button>
      </div>
    </div>
  );
}

// ─── KPI Card sub-component ──────────────────────────────────────────────────

function KPICard({ label, value, color, pulse }: { label: string; value: string | number; color: string; pulse?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {pulse && (
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" />
        </span>
      )}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
        <p className={`text-lg font-bold ${color}`}>{value}</p>
      </div>
    </div>
  );
}
